import { ColumnDef, RowData, TableMeta } from '@tanstack/react-table';
import { Options } from 'react-virtual';
import { HealthIconCellRendererProps } from '../../health/HealthIconCellRenderer';
import { ECPLinkRendererProps } from '../link/ECPLinkRenderer';
import { SparklineCellRendererProps } from '../cartesian/SparklineCellRenderer';

/** DATA **/
export type ObjectType = Record<string, unknown>;

export const CellType = {
  NUMBER: 'number',
  STRING: 'string',
  BOOLEAN: 'boolean',
  TIMESTAMP: 'timestamp', // timestamp in ISO format
  DURATION: 'duration', // ISO 8601 duration
  TIMESERIES: 'timeseries', // array of objects containing a timestamp field
  ARRAY: 'array',
  OBJECT: 'object',
  NULL: 'null',
  UNKNOWN: 'unknown',
  HEALTH: 'health',
  ECP_LINK: 'ecpLink',
} as const;
export type CellTypeOption = (typeof CellType)[keyof typeof CellType];

/** TABLE **/
export type FormatAs = {
  type?: string; // "number.short"
  options?: Record<string, unknown>;
};

export type ValueFormatterOptions = {
  enabled?: boolean;
  formatAs?: FormatAs;
};

export type RowVirtualizationOptions = {
  enabled: boolean;
  options?: Partial<Options<unknown>>;
};

declare module '@tanstack/table-core' {
  interface ColumnMeta<TData extends RowData, TValue> {
    cellType?: CellTypeOption; // TODO: make this required after DataGrid is depecrated
    align?: ContentAlignmentOption;
    valueFormatter?: ValueFormatterOptions;
    cellTypeOptions?:
      | Omit<HealthIconCellRendererProps, 'value'>
      | Omit<ECPLinkRendererProps, 'id' | 'value'>
      | Omit<SparklineCellRendererProps, 'data' | 'summaryValue'>;
  }

  interface TableMeta<TData extends RowData> {
    enableStickyHeader?: boolean;
    enableValueFormatting?: boolean;
    rowVirtualization?: RowVirtualizationOptions;
  }
}

// https://tanstack.com/table/v8/docs/api/core/column-def
export type TableColumn<TData, TValue = unknown> = Partial<ColumnDef<TData, TValue>> & {
  id: string;
};

export type ColumnOverrides<TData> = Record<string, Partial<ColumnDef<TData>>>;

// based on https://tanstack.com/table/v8/docs/api/core/table
export type DataTableProps<TData extends RowData> = {
  data: TData[];
  columnOverrides?: ColumnOverrides<TData>;
  tableOptions?: Partial<TableMeta<TData>>;
};

/** STYLING **/
export const ContentAlignment = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} as const;
export type ContentAlignmentOption = (typeof ContentAlignment)[keyof typeof ContentAlignment];
